import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import {
  SecaoQuestionario,
  useFormulario,
} from "@/contexts/fiscalizacao/formulario";
import { AssinaturaRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/AssinaturaRelatorioAM/AssinaturaRelatorioAM";
import { FormDadosGeraisRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/FormDadosGeraisRelatorioAM/FormDadosGeraisRelatorioAM";
import { FormFiscalizacoes } from "@/contexts/fiscalizacao/relatorioAM/components/FormFiscalizacoes/FormFiscalizacoes";
import { ImpresssaoRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/ImpresssaoRelatorioAM/ImpresssaoRelatorioAM";
import { QuestionarioRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/QuestionarioRelatorioAM/QuestionarioRelatorioAM";
import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { RelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAM";
import { RelatorioAMFormValues } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAMFormValues";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { converterFileToBase64 } from "@/shared/utils/convertFileToBase64";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  CheckOutlined,
  CloseOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Input,
  Row,
  Typography,
} from "antd";
import { RcFile } from "antd/lib/upload";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { v4 as uuid } from "uuid";

export function NovoRelatorioAM() {
  const { isOnline } = useNetworkStatus();
  const relatorioRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm<RelatorioAMFormValues>();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { modal, notification, message } = App.useApp();
  const [relatorioMinuta, setRelatorioMinuta] = useState<RelatorioAM | null>(
    null
  );
  const [secoesQuestionario, setSecoesQuestionario] = useState<
    SecaoQuestionario[]
  >([]);
  const [isLoadingQuestionario, setIsLoadingQuestionario] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { listaEstabelecimentos } = useEstabelecimento();
  const { listaFormularios } = useFormulario();
  const { submitRelatorioAMApi, addRelatorioAM, validateFinalizadoRelatorio } =
    useRelatorioAM();

  const handleBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    modal.confirm({
      title: "Cancelar",
      content: "Tem certeza que deseja cancelar o cadastro?",
      okText: "Sim",
      okType: "danger",
      cancelText: "Nao",
      onOk: () => {
        handleBack();
      },
    });
  };

  const criarArquivoComNovoNome = (arquivo: RcFile, novoNome: string): File => {
    return new File([arquivo], novoNome, {
      type: arquivo.type,
      lastModified: arquivo.lastModified,
    });
  };

  const buildRelatorioRequest = (
    values: RelatorioAMFormValues
  ): RelatorioAM | null => {
    const formularioSelected = listaFormularios.find(
      (formulario) => formulario.id === values.formulario.id
    );

    const estabelecimentoSelected = listaEstabelecimentos.find(
      (estabelecimento) => estabelecimento.id === values.estabelecimento.id
    );

    const pessoaInspecaoSelected =
      estabelecimentoSelected?.pessoasInspecao.find(
        (pessoa) => pessoa.id === values.inspetor.id
      );

    const proprietarioSelected =
      estabelecimentoSelected?.sociosProprietarios?.find(
        (proprietario) => proprietario.id === values.proprietario.id
      );

    if (
      !proprietarioSelected ||
      !pessoaInspecaoSelected ||
      !estabelecimentoSelected ||
      !formularioSelected
    ) {
      notification.error({
        message: "Não foi possivel salvar o relatório",
        description:
          "Não foi possível encontrar todas as informações selecionadas.",
      });
      return null;
    }

    return {
      formulario: formularioSelected,
      estabelecimento: estabelecimentoSelected,
      inspetor: pessoaInspecaoSelected,
      proprietario: proprietarioSelected,
      responsavelTecnico: values.responsavelTecnico || "",
      fiscalizacoes: (values.fiscalizacoes || []).map((fiscalizacao) => ({
        data: fiscalizacao.data?.format("YYYY-MM-DD") || null,
        horaFinal: fiscalizacao.horaFinal?.format("HH:mm") || null,
        horaInicial: fiscalizacao.horaInicial?.format("HH:mm") || null,
        placa: fiscalizacao.placa || "",
      })),
      observacao: values.observacao || "",
      acaoFiscal: values.acaoFiscal || "",
      conclusao: values.conclusao || "",
      assinaturas: (values.assinaturas || []).map((assinante) => ({
        nome: assinante.nome || "",
        cpf: (assinante.cpf || "").replace(/\D/g, ""),
        matricula: assinante.matricula || "",
        funcao: assinante.funcao || "",
        externo: assinante.externo || false,
        assinatura: assinante.assinatura || null,
        statusAssinatura: assinante.statusAssinatura || "ASSINADO_FISICAMENTE",
      })),
      situacao: "PENDENTE",
      dataMonitoria: dayjs().format("YYYY-MM-DD"),
      itens: (values.questionarios || [])
        .filter((quest) => quest.pergunta?.id)
        .map((quest) => {
          const pergunta = formularioSelected.perguntas.find(
            (p) => p.pergunta?.id === quest.pergunta.id
          );

          const alternativa = pergunta?.pergunta?.alternativas.find(
            (a) => a.id === quest.alternativa?.id
          );

          const uuidItem = uuid();

          return {
            uuid: uuidItem,
            dataResposta: quest.dataResposta
              ? quest.dataResposta
              : "1970-01-01",
            resposta: quest.observacao || "",
            secao: pergunta!.secao!,
            alternativa,
            pergunta: pergunta!.pergunta,
            status: "AGUARDANDO_PLANO_DE_ACAO",
            peso: pergunta?.peso!,
            anexos: (quest.anexo || []).map((anexo) => ({
              nomeArquivo: `${uuidItem}_${anexo.name}`,
              nomeUpload: anexo.uid,
            })),
          };
        }),
    };
  };

  const handleSave = async () => {
    try {
      const values = form.getFieldsValue();

      // Validações mínimas para gravar (apenas campos essenciais)
      if (
        !values.formulario?.id ||
        !values.estabelecimento?.id ||
        !values.inspetor?.id ||
        !values.proprietario?.id ||
        !values.responsavelTecnico?.trim()
      ) {
        notification.error({
          message: "Campos obrigatórios não preenchidos",
          description:
            "Por favor, preencha pelo menos o formulário, estabelecimento, inspetor, proprietário e responsável técnico.",
        });
        return;
      }

      const request = buildRelatorioRequest(values);
      if (!request) return;

      if (validateFinalizadoRelatorio(request)) {
        request.situacao = "FINALIZADO";
      }

      // console.log("Request para gravar:", request);

      if (!isOnline) {
        // Processar anexos offline se necessário
        const itensComAnexosBase64 = await Promise.all(
          request.itens.map(async (item) => {
            if (item.anexos.length > 0) {
              const anexosRcFile: RcFile[] =
                values.questionarios
                  ?.find((q) => q.pergunta.id === item.pergunta?.id)
                  ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

              if (anexosRcFile.length > 0) {
                const anexosBase64 = await Promise.all(
                  anexosRcFile.map((anexo) => converterFileToBase64(anexo))
                );

                item.anexos.forEach((anexo, index) => {
                  if (anexosBase64[index]) {
                    anexo.arquivoBase64 = anexosBase64[index];
                  }
                });
              }
            }
            return item;
          })
        );

        request.itens = itensComAnexosBase64;

        addRelatorioAM(request);

        notification.success({
          message: "Relatório gravado com sucesso",
          description:
            "O relatório foi salvo como rascunho. Você pode continuar editando posteriormente.",
        });

        saveAs(
          new Blob([JSON.stringify(request)], {
            type: "text/plain;charset=utf-8",
          }),
          "backup_relatorio_auditoria_monitoria-" + Date.now() + ".txt"
        );

        handleBack();
        return;
      }

      message.loading({
        content: "Gravando relatório...",
        key: "savingRelatorio",
        duration: 0,
      });

      setIsSubmitting(true);

      try {
        const formData = new FormData();
        formData.append(
          "ramRequest",
          new Blob([JSON.stringify(request)], {
            type: "application/json",
          })
        );

        // Processar anexos se houver
        const anexosRequest: { arquivo: File; nomeArquivo: string }[] = [];

        for (const item of request.itens) {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] =
              values.questionarios
                ?.find((q) => q.pergunta.id === item.pergunta?.id)
                ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

            anexosRcFile.forEach((anexoRc, index) => {
              const nomeArquivo =
                item.anexos[index]?.nomeArquivo ||
                `${item.uuid}_${anexoRc.name}`;
              anexosRequest.push({
                arquivo: criarArquivoComNovoNome(anexoRc, nomeArquivo),
                nomeArquivo,
              });
            });
          }
        }

        anexosRequest.forEach((anexo) => {
          formData.append("anexos", anexo.arquivo, anexo.nomeArquivo);
        });

        const result = await submitRelatorioAMApi(formData);

        notification.success({
          message: "Relatório gravado com sucesso",
          description: "O relatório foi gravado no servidor.",
        });

        const itensComAnexosBase64 = await Promise.all(
          request.itens.map(async (item) => {
            if (item.anexos.length > 0) {
              const anexosRcFile: RcFile[] =
                values.questionarios
                  ?.find((q) => q.pergunta.id === item.pergunta?.id)
                  ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

              if (anexosRcFile.length > 0) {
                const anexosBase64 = await Promise.all(
                  anexosRcFile.map((anexo) => converterFileToBase64(anexo))
                );

                item.anexos.forEach((anexo, index) => {
                  if (anexosBase64[index]) {
                    anexo.arquivoBase64 = anexosBase64[index];
                  }
                });
              }
            }
            return item;
          })
        );

        message.destroy("savingRelatorio");
        setIsSubmitting(false);

        request.itens = itensComAnexosBase64;

        addRelatorioAM({ ...request, id: result.id, isUpdated: true });
        handleBack();
      } catch (error) {
        console.error(error);
        setIsSubmitting(false);
        message.destroy("savingRelatorio");
        const messageError = formatMessageError(error);
        notification.error({
          message: "Não foi possível gravar o relatório",
          description: messageError,
        });
      }
    } catch (error) {
      console.error("Erro ao gravar:", error);
      notification.error({
        message: "Erro ao gravar",
        description: "Por favor, verifique os campos preenchidos.",
      });
    }
  };

  const handleFinalize = async (values: RelatorioAMFormValues) => {
    // validacao se a fiscalizacao tem data, hora inicial e hora final
    const fiscalizacoesInvalidas = (values.fiscalizacoes || []).filter(
      (fiscalizacao) =>
        !fiscalizacao.data ||
        !fiscalizacao.horaInicial ||
        !fiscalizacao.horaFinal
    );
    if (fiscalizacoesInvalidas.length > 0) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description:
          "Existem fiscalizações sem data, hora inicial ou hora final. Por favor, preencha todas as fiscalizações.",
      });
      return;
    }

    // Validações completas para finalizar (comportamento atual)
    if (!values.questionarios || values.questionarios.length === 0) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description: "Nenhum questionário respondido.",
      });
      return;
    }

    // Validar se todas as perguntas obrigatórias foram respondidas
    const perguntasNaoRespondidas = values.questionarios.filter(
      (quest) => !quest.alternativa?.id
    );

    if (perguntasNaoRespondidas.length > 0) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description:
          "Existem perguntas sem alternativa selecionada. Por favor, responda todas as perguntas.",
      });
      return;
    }

    // Validar assinaturas
    if (!values.assinaturas || values.assinaturas.length === 0) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description: "É necessário adicionar pelo menos uma assinatura.",
      });
      return;
    }

    const assinaturaInvalida = values.assinaturas.find(
      (assinante) =>
        assinante.statusAssinatura === "ASSINADO_DIGITALMENTE" &&
        !assinante.assinatura
    );

    if (assinaturaInvalida) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description:
          "Existem assinaturas digitais sem a assinatura preenchida. Por favor, verifique.",
      });
      return;
    }

    // Validar campos obrigatórios
    if (!values.conclusao?.trim()) {
      notification.error({
        message: "Não foi possivel finalizar o relatório",
        description: "O campo conclusão é obrigatório.",
      });
      return;
    }

    const request = buildRelatorioRequest(values);
    if (!request) return;

    request.situacao = "FINALIZADO";

    if (!isOnline) {
      const itensComAnexosBase64 = await Promise.all(
        request.itens.map(async (item) => {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] = values.questionarios
              ?.find((q) => q.pergunta.id === item.pergunta?.id)
              ?.anexo.map((anexo) => anexo.originFileObj as RcFile) as RcFile[];
            const anexosBase64 = await Promise.all(
              anexosRcFile.map((anexo) => converterFileToBase64(anexo))
            );
            item.anexos.forEach((anexo, index) => {
              anexo.arquivoBase64 = anexosBase64[index];
            });
          }
          return item;
        })
      );

      request.itens = itensComAnexosBase64;
      addRelatorioAM(request);

      notification.success({
        message: "Relatório de Auditoria e Monitoria finalizado com sucesso",
        description:
          "O relatório foi salvo localmente e ficará com status de pendente até ser sincronizado com o servidor.",
      });

      saveAs(
        new Blob([JSON.stringify(request)], {
          type: "text/plain;charset=utf-8",
        }),
        "backup_relatorio_auditoria_monitoria-" + Date.now() + ".txt"
      );

      handleBack();
      return;
    }

    message.loading({
      content: "Finalizando relatório...",
      key: "finalizingRelatorio",
      duration: 0,
    });

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append(
        "ramRequest",
        new Blob([JSON.stringify(request)], {
          type: "application/json",
        })
      );

      // Processar anexos
      const anexosItem = await Promise.all(
        request.itens.map(async (item) => {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] = values.questionarios
              ?.find((q) => q.pergunta.id === item.pergunta?.id)
              ?.anexo.map((anexo) => anexo.originFileObj as RcFile) as RcFile[];

            return anexosRcFile.map((anexoRc, index) => {
              const novoNome = item.anexos[index].nomeArquivo ?? "";
              return {
                arquivo: criarArquivoComNovoNome(anexoRc, novoNome),
                nomeArquivo: novoNome,
              };
            });
          }
          return [];
        })
      );

      const anexosRequest = anexosItem.flatMap((item) => item);
      anexosRequest.forEach((anexo) => {
        formData.append("anexos", anexo.arquivo as File, anexo.nomeArquivo);
      });

      const result = await submitRelatorioAMApi(formData);

      notification.success({
        message: "Relatório finalizado com sucesso",
        description: "O relatório foi sincronizado com o servidor.",
      });

      const itensComAnexosBase64 = await Promise.all(
        request.itens.map(async (item) => {
          if (item.anexos.length > 0) {
            const anexosRcFile: RcFile[] =
              values.questionarios
                ?.find((q) => q.pergunta.id === item.pergunta?.id)
                ?.anexo?.map((anexo) => anexo.originFileObj as RcFile) || [];

            if (anexosRcFile.length > 0) {
              const anexosBase64 = await Promise.all(
                anexosRcFile.map((anexo) => converterFileToBase64(anexo))
              );

              item.anexos.forEach((anexo, index) => {
                if (anexosBase64[index]) {
                  anexo.arquivoBase64 = anexosBase64[index];
                }
              });
            }
          }
          return item;
        })
      );

      request.itens = itensComAnexosBase64;

      message.destroy("finalizingRelatorio");
      setIsSubmitting(false);

      addRelatorioAM({ ...request, id: result.id, isUpdated: true });
      handleBack();
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      message.destroy("finalizingRelatorio");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível finalizar o relatório",
        description: messageError,
      });
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const values = form.getFieldsValue();

      const request = buildRelatorioRequest(values);
      if (!request) return;

      // Para preview, não importa se é rascunho ou não
      const requestPreview: RelatorioAM = {
        ...request,
        itens: request.itens.map((item) => ({
          ...item,
          anexos: [], // Preview sem anexos
        })),
      };

      setRelatorioMinuta(requestPreview);
    } catch (error) {
      console.error("Validation Failed:", error);
      notification.error({
        message: "Não foi possível gerar a prévia",
        description: "Por favor, verifique os campos preenchidos.",
      });
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: relatorioRef,
    documentTitle: "ram_minuta",
    suppressErrors: true,
    preserveAfterPrint: true,
    onAfterPrint: () => setRelatorioMinuta(null),
  });

  useEffect(() => {
    if (relatorioMinuta) {
      handlePrint();
    }
  }, [relatorioMinuta, handlePrint]);

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Novo Relatório de Auditoria e Monitoria";
  }, []);

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Novo Relatório de Auditoria e Monitoria
      </Typography.Title>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        onFinish={handleFinalize}
        initialValues={{
          fiscalizacoes: [
            {
              data: undefined,
            },
          ],
          assinaturas: [
            {
              nome: undefined,
            },
          ],
        }}
        onFinishFailed={() => {
          notification.error({
            message: "Erro ao finalizar o relatório",
            description: "Por favor, verifique os campos obrigatórios.",
          });
        }}
      >
        <FormDadosGeraisRelatorioAM
          form={form}
          setSecoesQuestionario={setSecoesQuestionario}
          setIsLoadingQuestionario={setIsLoadingQuestionario}
        />

        <FormFiscalizacoes />

        <Divider />

        <Form.Item
          label="Conclusão"
          name="conclusao"
          rules={[
            { required: true, message: "Campo obrigatório para finalizar" },
          ]}
        >
          <Input.TextArea placeholder="Informe a conclusão do relatório" />
        </Form.Item>

        <Form.Item label="Ação Fiscal" name="acaoFiscal">
          <Input.TextArea placeholder="Informe a ação fiscal do relatório" />
        </Form.Item>

        <QuestionarioRelatorioAM
          secoesQuestionario={secoesQuestionario}
          form={form}
          isLoadingQuestionario={isLoadingQuestionario}
        />

        <AssinaturaRelatorioAM form={form} />

        <Divider />

        <Row justify="center" gutter={[10, 10]} style={{ marginTop: 48 }}>
          <Col>
            <Button
              danger
              htmlType="button"
              ghost
              icon={<CloseOutlined />}
              onClick={handleCancel}
              style={{ width: 180 }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<FilePdfOutlined />}
              htmlType="button"
              style={{ width: 180 }}
              onClick={handleGeneratePreview}
              disabled={isSubmitting}
            >
              Gerar Prévia
            </Button>
          </Col>

          <Col>
            <Button
              icon={<SaveOutlined />}
              htmlType="button"
              style={{ width: 180 }}
              onClick={handleSave}
              disabled={isSubmitting}
              loading={isSubmitting}
              type="primary"
              ghost
            >
              Gravar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<CheckOutlined />}
              type="primary"
              htmlType="submit"
              style={{ width: 180 }}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Finalizar
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ display: "none" }}>
        <ImpresssaoRelatorioAM
          ref={relatorioRef}
          item={relatorioMinuta}
          marcadagua="MINUTA"
        />
      </div>
    </>
  );
}
